import { apiGet, apiPatch, apiPost, apiDelete } from "../lib/api-client";
import type { StoreItemKind, StoreItemMetadata, StoreItemRequirement, StoreItemRarity } from "./store";
import type { ActivityReviewResponse } from "./activities";
import type { PlanSource, FullHistoryState, AchievementsResponse, CollectionsResponse, OnboardingState } from "@atlas/shared";
import type { ProfileStatsWithCosmeticsResponse } from "./profile";

// ─── Stats ────────────────────────────────────────────────────────────────────

export interface AdminStats {
  totalUsers: number;
  verifiedUsers: number;
  newUsers7d: number;
  newUsers30d: number;
  planMix: { free: number; pro: number; lifetime: number };
  supporters: number;
  proViaKey: number;
  /** Seen in the app (sessions.last_seen_at), not merely synced - spec 14. */
  activeUsers7d: number;
  activeUsers30d: number;
  /** Had an activity land in 30d without opening Atlas. */
  syncingOnly30d: number;
  seenByClient30d: { web: number; ios: number; android: number; unknown: number };
  totalActivities: number;
  activities7d: number;
  gpxVsStravaSplit: { gpx: number; strava: number };
  totalSummitsLogged: number;
  achievementsUnlocked7d: number;
  content: {
    peaks: number;
    trails: number;
    landmarks: number;
    regions: number;
    collections: number;
  };
  unprocessedActivities: number;
}

export interface TimeseriesPoint {
  date: string;
  value: number;
}

export function fetchAdminStats(): Promise<{ stats: AdminStats }> {
  return apiGet("/admin/stats");
}

export function fetchAdminTimeseries(
  metric: "signups" | "activities" | "revenue",
  days = 90,
): Promise<{ points: TimeseriesPoint[]; error?: string }> {
  return apiGet(`/admin/stats/timeseries?metric=${metric}&days=${days}`);
}

// ─── Users ────────────────────────────────────────────────────────────────────

export interface AdminUserSummary {
  id: string;
  email: string;
  name: string | null;
  plan: string;
  billingStatus: string;
  planExpiresAt: string | null;
  isAdmin: boolean;
  emailVerified: boolean;
  createdAt: string;
  /** Legacy GREATEST(session created, activity created) - kept for sorting. */
  lastActiveAt: string | null;
  /** Last foreground request (spec 14). Null until seen post-deploy. */
  lastSeenAt: string | null;
  lastSeenClient: UsageClient | null;
  /** Last activity landed (Strava webhook / GPX / Health). */
  lastSyncAt: string | null;
  activityCount: number;
  outdoorScore: number;
}

export type UsageClient = "web" | "ios" | "android";

export interface AdminUserDetail {
  id: string;
  email: string;
  name: string | null;
  plan: string;
  billingStatus: string;
  planExpiresAt: string | null;
  isAdmin: boolean;
  emailVerified: boolean;
  createdAt: string;
  lastActiveAt: string | null;
  lastSeenAt: string | null;
  lastSyncAt: string | null;
  activityCount: number;
  outdoorScore: number;
  billingCustomerId: string | null;
  billingSubscriptionId: string | null;
  /** Where Pro comes from (spec 24). */
  planSource: PlanSource;
}

export interface AdminUserIdentity {
  provider: string;
  connectedAt: string;
}

export interface AdminUserActivity {
  id: string;
  name: string;
  sourceType: string;
  startedAt: string;
  distanceM: number | null;
  processed: boolean;
}

export interface AdminUserRedeemedKey {
  id: string;
  code: string;
  plan: string;
  durationDays: number | null;
  redeemedAt: string | null;
}

export interface AdminUserListParams {
  search?: string;
  plan?: string;
  sort?: "created_at" | "last_active" | "last_seen" | "last_sync";
  order?: "asc" | "desc";
  limit?: number;
  offset?: number;
  /** Include soft-deleted (anonymised) accounts, hidden by default. */
  includeDeleted?: boolean;
}

export function fetchAdminUsers(
  params: AdminUserListParams = {},
): Promise<{ users: AdminUserSummary[]; total: number }> {
  const query = new URLSearchParams();
  if (params.search) query.set("search", params.search);
  if (params.plan) query.set("plan", params.plan);
  if (params.sort) query.set("sort", params.sort);
  if (params.order) query.set("order", params.order);
  if (params.includeDeleted) query.set("deleted", "1");
  query.set("limit", String(params.limit ?? 50));
  query.set("offset", String(params.offset ?? 0));
  return apiGet(`/admin/users?${query.toString()}`);
}

export function fetchAdminUser(id: string): Promise<{
  user: AdminUserDetail;
  identities: AdminUserIdentity[];
  recentActivities: AdminUserActivity[];
  redeemedKey: AdminUserRedeemedKey | null;
}> {
  return apiGet(`/admin/users/${id}`);
}

export interface AdminUserActivityRow {
  id: string;
  name: string;
  sourceType: string;
  startedAt: string;
  durationSeconds: number | null;
  distanceM: number | null;
  elevationGainM: number | null;
  processedAt: string | null;
  processed: boolean;
}

export function fetchAdminUserActivities(
  id: string,
  params: { limit?: number; offset?: number } = {},
): Promise<{ activities: AdminUserActivityRow[]; total: number }> {
  const query = new URLSearchParams();
  query.set("limit", String(params.limit ?? 50));
  query.set("offset", String(params.offset ?? 0));
  return apiGet(`/admin/users/${id}/activities?${query.toString()}`);
}

export function fetchAdminUserActivityReview(
  userId: string,
  activityId: string,
): Promise<ActivityReviewResponse> {
  return apiGet(`/admin/users/${userId}/activities/${activityId}/review`);
}

export function fetchAdminUserShowcaseUrl(id: string): Promise<{ url: string }> {
  return apiGet(`/admin/users/${id}/showcase-url`);
}

// Plan changes moved to the subscription panel (spec 24); the endpoint
// rejects plan / planExpiresAt.
export interface AdminUserPatch {
  isAdmin?: boolean;
  emailVerified?: boolean;
}

export function patchAdminUser(
  id: string,
  data: AdminUserPatch,
): Promise<{ user: AdminUserDetail; stripeManaged: boolean }> {
  return apiPatch(`/admin/users/${id}`, data);
}

export function revokeAdminUserSessions(id: string): Promise<{ ok: boolean; revoked: number }> {
  return apiPost(`/admin/users/${id}/sessions/revoke`);
}

export function deleteAdminUser(id: string): Promise<{ ok: boolean }> {
  return apiDelete(`/admin/users/${id}`);
}

// ─── Audit log ────────────────────────────────────────────────────────────────

export interface AdminAuditEntry {
  id: string;
  adminUserId: string;
  adminEmail: string | null;
  action: string;
  targetType: string;
  targetId: string | null;
  detail: unknown;
  createdAt: string;
}

export function fetchAdminAuditLog(limit = 100): Promise<{ entries: AdminAuditEntry[] }> {
  return apiGet(`/admin/audit-log?limit=${limit}`);
}

// ─── Store items (STO-2 admin) ─────────────────────────────────────────────────

export interface AdminStoreItem {
  id: string;
  slug: string;
  name: string;
  description: string;
  kind: StoreItemKind;
  priceWaypoints: number | null;
  pricePence: number | null;
  requirement: StoreItemRequirement | null;
  imageUrl: string | null;
  metadata: StoreItemMetadata;
  stock: number | null;
  active: boolean;
  featured: boolean;
  sortOrder: number;
  rarity: StoreItemRarity;
  createdAt: string;
  ownedCount: number;
}

export function fetchAdminStoreItems(): Promise<{ items: AdminStoreItem[] }> {
  return apiGet("/admin/store/items");
}

export interface AdminStoreItemCreate {
  slug: string;
  name: string;
  description: string;
  kind: StoreItemKind;
  priceWaypoints?: number | null;
  pricePence?: number | null;
  requirement?: StoreItemRequirement | null;
  imageUrl?: string | null;
  metadata?: StoreItemMetadata;
  stock?: number | null;
  active?: boolean;
  featured?: boolean;
  sortOrder?: number;
  rarity?: StoreItemRarity;
}

export function createAdminStoreItem(data: AdminStoreItemCreate): Promise<{ item: AdminStoreItem }> {
  return apiPost("/admin/store/items", data);
}

export type AdminStoreItemPatch = Partial<Omit<AdminStoreItemCreate, "slug" | "kind">>;

export function patchAdminStoreItem(
  id: string,
  data: AdminStoreItemPatch,
): Promise<{ item: AdminStoreItem }> {
  return apiPatch(`/admin/store/items/${id}`, data);
}

// ─── Store orders (fulfilment queue) ───────────────────────────────────────────

export type AdminOrderStatus = "pending" | "paid" | "fulfilled" | "cancelled" | "refunded";

export interface AdminOrder {
  id: string;
  userId: string;
  userEmail: string | null;
  status: AdminOrderStatus;
  totalPence: number | null;
  totalWaypoints: number | null;
  stripeCheckoutSessionId: string | null;
  shippingAddress: unknown;
  fulfilmentNote: string | null;
  createdAt: string;
  updatedAt: string;
  items: { name: string; quantity: number }[];
}

export function fetchAdminStoreOrders(status?: string): Promise<{ orders: AdminOrder[] }> {
  return apiGet(`/admin/store/orders${status ? `?status=${status}` : ""}`);
}

export function patchAdminStoreOrder(
  id: string,
  data: { status?: AdminOrderStatus; fulfilmentNote?: string | null },
): Promise<{ order: AdminOrder }> {
  return apiPatch(`/admin/store/orders/${id}`, data);
}

// ─── Grant Waypoints (support tool) ────────────────────────────────────────────

export function grantAdminUserWaypoints(
  id: string,
  data: { delta: number; note?: string },
): Promise<{ waypoints: number }> {
  return apiPost(`/admin/users/${id}/waypoints`, data);
}

// ─── Social moderation (SOC-6) ──────────────────────────────────────────────────

export interface AdminReportedPost {
  postId: string;
  reportCount: number;
  reasons: string[];
  post: {
    id: string;
    kind: string;
    body: string | null;
    authorUsername: string | null;
    authorEmail: string;
    createdAt: string;
    deletedAt: string | null;
  };
  latestReportAt: string;
}

export function fetchSocialReports(): Promise<{ reports: AdminReportedPost[] }> {
  return apiGet("/admin/social/reports");
}

export function dismissPostReports(postId: string): Promise<{ ok: boolean }> {
  return apiPost(`/admin/social/reports/${postId}/dismiss`);
}

export function adminDeletePost(postId: string): Promise<{ ok: boolean }> {
  return apiDelete(`/admin/social/posts/${postId}`);
}

// ─── Usage analytics (spec 14 / ATL-70) ──────────────────────────────────────

export interface UsageQuery {
  days?: number;
  includeAdmins?: boolean;
  client?: UsageClient | null;
}

function usageParams(q: UsageQuery = {}, extra: Record<string, string> = {}): string {
  const params = new URLSearchParams(extra);
  if (q.days) params.set("days", String(q.days));
  if (q.includeAdmins) params.set("admins", "1");
  if (q.client) params.set("client", q.client);
  const str = params.toString();
  return str ? `?${str}` : "";
}

export interface UsageSummary {
  days: number;
  includeAdmins: boolean;
  views: number;
  visitors: number;
  visits: number;
  visitsPerVisitor: number;
  viewsPerVisit: number;
  previous: { views: number; visitors: number };
  byClient: Record<UsageClient, { views: number; visitors: number }>;
  topScreens: { name: string; views: number; visitors: number }[];
  collectingSince: string | null;
}

export function fetchUsageSummary(q: UsageQuery = {}): Promise<{ summary: UsageSummary }> {
  return apiGet(`/admin/usage/summary${usageParams(q)}`);
}

export interface UsageTimeseriesPoint {
  date: string;
  web: number;
  ios: number;
  android: number;
  total: number;
}

export function fetchUsageTimeseries(
  q: UsageQuery = {},
  metric: "visitors" | "views" = "visitors",
): Promise<{ metric: string; points: UsageTimeseriesPoint[] }> {
  return apiGet(`/admin/usage/timeseries${usageParams(q, { metric })}`);
}

export interface UsageScreenRow {
  name: string;
  views: number;
  visitors: number;
  share: number;
  byClient: Record<UsageClient, number>;
  paths: string[];
}

export function fetchUsageScreens(
  q: UsageQuery = {},
): Promise<{ days: number; totalViews: number; screens: UsageScreenRow[] }> {
  return apiGet(`/admin/usage/screens${usageParams(q)}`);
}

export interface UsageHeatCell {
  dow: number;
  hour: number;
  views: number;
  visitors: number;
}

export function fetchUsageHeatmap(
  q: UsageQuery = {},
): Promise<{ days: number; timezone: string; cells: UsageHeatCell[] }> {
  return apiGet(`/admin/usage/heatmap${usageParams(q)}`);
}

export interface UsageCohort {
  cohortWeek: string;
  size: number;
  retained: number[];
}

export function fetchUsageRetention(
  weeks = 8,
  includeAdmins = false,
): Promise<{ weeks: number; cohorts: UsageCohort[] }> {
  return apiGet(
    `/admin/usage/retention?weeks=${weeks}${includeAdmins ? "&admins=1" : ""}`,
  );
}

export interface AdminUserUsage {
  days: number;
  views: number;
  visits: number;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  byClient: Record<UsageClient, number>;
  topScreens: { name: string; views: number; lastAt: string }[];
  recentVisits: {
    startedAt: string;
    endedAt: string;
    client: UsageClient;
    views: number;
    screens: string[];
  }[];
  sessions: {
    id: string;
    client: UsageClient | null;
    appVersion: string | null;
    createdAt: string;
    lastSeenAt: string | null;
    expiresAt: string;
  }[];
}

export function fetchAdminUserUsage(userId: string, days = 30): Promise<{ usage: AdminUserUsage }> {
  return apiGet(`/admin/usage/users/${userId}?days=${days}`);
}

// ─── Activation funnel (spec 15) ─────────────────────────────────────────────

export const FUNNEL_STAGES = [
  "signed_up",
  "verified",
  "source_connected",
  "first_activity_processed",
  "first_unlock",
  "returned_day7",
  "pro",
] as const;
export type FunnelStage = (typeof FUNNEL_STAGES)[number];

export interface FunnelCohort {
  week: string;
  counts: Record<FunnelStage, number>;
  medianUnlockHours: number | null;
}

export interface FunnelStuckUser {
  id: string;
  email: string;
  name: string | null;
  createdAt: string;
  firstProcessedAt: string | null;
  lastSeenAt: string | null;
}

export interface AdminFunnel {
  weeks: number;
  stages: readonly FunnelStage[];
  cohorts: FunnelCohort[];
  totals: Record<FunnelStage, number>;
  medianUnlockHours: number | null;
  unlockedUsers: number;
  stuck: {
    noSource: FunnelStuckUser[];
    noReturn: FunnelStuckUser[];
    limit: number;
    noSourceDays: number;
    returnGapHours: number;
  };
}

export function fetchAdminFunnel(weeks = 12): Promise<{ funnel: AdminFunnel }> {
  return apiGet(`/admin/funnel?weeks=${weeks}`);
}

// ─── Pipeline health (ops hygiene pass) ───────────────────────────────────────

export interface PipelineQueueDepth {
  name: string;
  visible: number;
  notVisible: number;
}

export interface PipelineQueue {
  key: string;
  label: string;
  configured: boolean;
  queue: PipelineQueueDepth | null;
  dlq: PipelineQueueDepth | null;
  error: string | null;
}

export interface PipelineActivityCounts {
  total: number;
  processed: number;
  queued: number;
  stuck: number;
}

export interface PipelineStuckActivity {
  id: string;
  name: string;
  sourceType: string;
  createdAt: string;
  userId: string;
  userEmail: string | null;
  userName: string | null;
  hasGeom: boolean;
}

export interface PipelineDlqFailure {
  messageId: string;
  activityId: string | null;
  activityName: string | null;
  sourceType: string | null;
  userId: string | null;
  userEmail: string | null;
  userName: string | null;
  activityDeleted: boolean;
  sentAt: string | null;
  receiveCount: number;
}

export interface AdminPipeline {
  generatedAt: string;
  stuckAfterMinutes: number;
  queues: PipelineQueue[];
  activities: { total: PipelineActivityCounts; last24h: PipelineActivityCounts };
  stuck: PipelineStuckActivity[];
  dlqFailures: PipelineDlqFailure[];
}

export function fetchAdminPipeline(): Promise<AdminPipeline> {
  return apiGet("/admin/pipeline");
}

export function reprocessAdminActivity(activityId: string): Promise<{ ok: true; mode: "queued" | "inline" }> {
  return apiPost(`/admin/pipeline/activities/${activityId}/reprocess`);
}

export function reprocessAdminUserActivities(
  userId: string,
): Promise<{ ok: true; queued: number; skippedNoGeom: number; mode: "queued" | "inline" }> {
  return apiPost(`/admin/pipeline/users/${userId}/reprocess`);
}

export function redriveAdminQueue(
  key: string,
): Promise<{ ok: true; queue: string; approximateMessages: number; taskHandle: string | null }> {
  return apiPost(`/admin/pipeline/queues/${key}/redrive`);
}

// ─── Subscriptions (spec 24 / ATL-104) ────────────────────────────────────────

export interface BillingEventRow {
  id: string;
  provider: string;
  kind: string;
  providerObjectId: string | null;
  productId: string | null;
  interval: string | null;
  amountPence: number | null;
  currency: string | null;
  periodEnd: string | null;
  adminUserId: string | null;
  adminEmail: string | null;
  reason: string | null;
  occurredAt: string;
}

export interface SubscriptionPanel {
  entitlement: {
    isPro: boolean;
    plan: string;
    planSource: PlanSource;
    planExpiresAt: string | null;
    billingStatus: string;
    historyCutoff: string | null;
    fullHistory: FullHistoryState;
  };
  stripe: {
    customerId: string | null;
    subscriptionId: string | null;
    dashboardUrl: string | null;
    paymentFailures30d: number;
    live: {
      status: string;
      priceId: string | null;
      interval: string | null;
      amountPence: number | null;
      currency: string;
      currentPeriodEnd: string | null;
      cancelAtPeriodEnd: boolean;
      cancelAt: string | null;
      trialEnd: string | null;
      latestInvoice: {
        id: string | null;
        status: string | null;
        amountPaid: number;
        hostedUrl: string | null;
        paidAt: string | null;
      } | null;
    } | null;
  } | null;
  apple: {
    originalTransactionId: string;
    consoleUrl: string;
    configured: boolean;
    live: { active: boolean; productId: string | null; expiresAt: string | null; environment: string } | null;
  } | null;
  google: {
    purchaseToken: string;
    consoleUrl: string;
    configured: boolean;
    live: { active: boolean; productId: string | null; expiresAt: string | null; acknowledged: boolean } | null;
  } | null;
  key: { code: string; durationDays: number | null; redeemedAt: string | null } | null;
  invite: { code: string; label: string | null; grantedExpiresAt: string; removedAt: string | null } | null;
  grants: BillingEventRow[];
  events: BillingEventRow[];
  conflicts: string[];
}

export function fetchSubscriptionPanel(userId: string): Promise<SubscriptionPanel> {
  return apiGet(`/admin/subscriptions/users/${userId}`);
}

export function stripeCancelSubscription(
  userId: string,
  body: { atPeriodEnd: boolean; reason: string },
): Promise<{ ok: boolean; atPeriodEnd: boolean; periodEnd: string | null }> {
  return apiPost(`/admin/subscriptions/users/${userId}/stripe/cancel`, body);
}

export function stripeRefundLatest(
  userId: string,
  body: { reason: string },
): Promise<{ ok: boolean; refundId: string; amountPence: number; currency: string }> {
  return apiPost(`/admin/subscriptions/users/${userId}/stripe/refund-latest`, body);
}

export function stripeExtendSubscription(
  userId: string,
  body: { days: number; reason: string },
): Promise<{ ok: boolean; periodEnd: string }> {
  return apiPost(`/admin/subscriptions/users/${userId}/stripe/extend`, body);
}

export function grantComplimentaryPro(
  userId: string,
  body: { days?: number; until?: string; reason: string; includeFullHistory?: boolean },
): Promise<{ ok: boolean; expiresAt: string; fullHistory: boolean }> {
  return apiPost(`/admin/subscriptions/users/${userId}/pro-grant`, body);
}

/** Spec 30: grant (source admin) or revoke the admin source of Full history. */
export function setAdminFullHistory(
  userId: string,
  body: { grant: boolean; reason: string },
): Promise<{ ok: boolean; fullHistory: { unlocked: boolean; source?: string | null } }> {
  return apiPost(`/admin/subscriptions/users/${userId}/full-history`, body);
}

export function extendComplimentaryPro(
  userId: string,
  body: { days: number; reason: string },
): Promise<{ ok: boolean; expiresAt: string }> {
  return apiPost(`/admin/subscriptions/users/${userId}/pro-extend`, body);
}

export function revokeComplimentaryPro(
  userId: string,
  body: { reason: string },
): Promise<{ ok: boolean }> {
  return apiPost(`/admin/subscriptions/users/${userId}/pro-revoke`, body);
}

export function clearStoreId(
  userId: string,
  body: { provider: "apple" | "google"; reason: string },
): Promise<{ ok: boolean }> {
  return apiPost(`/admin/subscriptions/users/${userId}/clear-store-id`, body);
}

export interface SubscriptionConflict {
  userId: string;
  email: string;
  name: string | null;
  plan: string;
  planSource: PlanSource;
  planExpiresAt: string | null;
  billingStatus: string;
  stores: string[];
}

export function fetchSubscriptionConflicts(): Promise<{ conflicts: SubscriptionConflict[] }> {
  return apiGet("/admin/subscriptions/conflicts");
}

export interface DunningRow {
  userId: string;
  email: string;
  name: string | null;
  planSource: PlanSource;
  billingStatus: string;
  planExpiresAt: string | null;
  state: "past_due" | "grace";
  since: string | null;
  emails: { kind: string; status: string; sentAt: string }[];
}

export function fetchSubscriptionDunning(): Promise<{ dunning: DunningRow[] }> {
  return apiGet("/admin/subscriptions/dunning");
}

// Revenue (spec 36): mirrors RevenueReport in packages/db/src/revenue.ts.
export type RevenueType = "subscription" | "full_history" | "store";

export interface RevenueEntry {
  id: string;
  occurredAt: string;
  provider: string;
  kind: "new_subscription" | "renewal" | "full_history" | "store" | "refund";
  type: RevenueType;
  product: "monthly" | "yearly" | "full_history" | "store" | "unknown";
  amountPence: number;
  listPrice: boolean;
  userId: string | null;
  userName: string | null;
  userEmail: string | null;
  counted: boolean;
  notCountedReason: "marked" | "test_purchase" | "admin_account" | null;
  note: string | null;
  marked: boolean;
}

export interface RevenueMonth {
  month: string;
  subscriptionsPence: number;
  fullHistoryPence: number;
  storePence: number;
  refundsPence: number;
  netPence: number;
  newSubscriptions: number;
  renewals: number;
  fullHistorySales: number;
  ended: number;
}

export interface PayingSubscriber {
  userId: string;
  userName: string | null;
  userEmail: string | null;
  provider: string;
  product: "monthly" | "yearly";
  monthlyPence: number;
  cancelling: boolean;
  renewsOrEndsAt: string | null;
}

export interface RevenueReport {
  generatedAt: string;
  totals: {
    allTimePence: number;
    thisMonthPence: number;
    last30DaysPence: number;
    refundsPence: number;
    byType: Record<RevenueType, number>;
    afterFeesPence: number;
  };
  subscriptions: {
    paying: PayingSubscriber[];
    monthlyCount: number;
    yearlyCount: number;
    monthlyValuePence: number;
    cancellingCount: number;
    endedLast30Days: number;
  };
  fullHistory: { soldCount: number; refundedCount: number; totalPence: number };
  gifted: { proTotal: number; proBySource: { source: string; count: number }[]; fullHistoryCount: number };
  months: RevenueMonth[];
  entries: RevenueEntry[];
}

export function fetchRevenueReport(months = 12): Promise<{ revenue: RevenueReport }> {
  return apiGet(`/admin/subscriptions/revenue?months=${months}`);
}

export function setRevenueEventExcluded(
  id: string,
  body: { excluded: boolean; reason?: string },
): Promise<{ ok: boolean }> {
  return apiPatch(`/admin/subscriptions/revenue/events/${id}`, body);
}

// ─── User profile view (read-only support view of one user's Atlas) ──────────

export interface AdminUserSyncRun {
  provider: string;
  status: string;
  startedAt: string;
  finishedAt: string | null;
  importedCount: number;
  skippedCount: number;
  error: string | null;
  failedLast30Days: number;
}

export interface AdminUserOverview {
  profile: { username: string | null; profileVisibility: string };
  stats: ProfileStatsWithCosmeticsResponse;
  health: {
    pendingActivities: number;
    oldestPendingAt: string | null;
    processedActivities: number;
    syncs: AdminUserSyncRun[];
    exclusions: Record<string, number>;
    excludedActivityKinds: string[];
    historyTruncatedAt: string | null;
    historyOldestSkippedAt: string | null;
    fullHistoryAt: string | null;
    fullHistorySource: string | null;
    pushPlatforms: { platform: string; lastSeenAt: string | null }[];
  };
  onboarding: OnboardingState;
  attribution: {
    referredBy: { name: string | null; username: string | null; at: string | null } | null;
    partner: { code: string; label: string | null; at: string | null } | null;
  };
  social: {
    posts: number;
    followers: number;
    following: number;
    checkins: number;
    goalsOpen: number;
    goalsDone: number;
    eventsDone: number;
    inventory: number;
    feedback: number;
    exploredCells: number;
    groups: { id: string; name: string; role: string; status: string }[];
  };
}

export interface AdminUserCollected {
  peaks: {
    total: number;
    items: { id: string; name: string; slug: string; elevationM: number; collectedAt: string; activityId: string | null; visits: number }[];
  };
  landmarks: {
    total: number;
    items: { id: string; name: string; slug: string; category: string; collectedAt: string; activityId: string | null; visits: number }[];
  };
  trails: { id: string; name: string; slug: string; distanceKm: number | null; pct: number; walkedKm: number | null; sections: number; sectionsDone: number }[];
  regions: { id: string; name: string; slug: string; country: string | null; coveragePct: number; updatedAt: string }[];
}

export function fetchAdminUserOverview(id: string): Promise<AdminUserOverview> {
  return apiGet(`/admin/users/${id}/overview`);
}

export function fetchAdminUserCollections(id: string): Promise<CollectionsResponse> {
  return apiGet(`/admin/users/${id}/collections`);
}

export function fetchAdminUserAchievements(id: string): Promise<AchievementsResponse> {
  return apiGet(`/admin/users/${id}/achievements`);
}

export function fetchAdminUserCollected(id: string): Promise<AdminUserCollected> {
  return apiGet(`/admin/users/${id}/collected`);
}
